import { css, html } from "lit";
import { IElementInternals } from "element-internals-polyfill";
import { BaseElement } from "./BaseElement.js";
import { chevron, IconWarning } from "./icons.js";
import {
  customElement,
  property,
  queryAssignedElements,
} from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";

@customElement("ac101-checkbox")
class AC101Checkbox extends BaseElement {
  static get styles() {
    return css`
      :host {
        display: block;
      }
      .toggle-button {
        border: none;
        background: none;
      }
      .filter-item-checkbox {
        display: grid !important;
        grid-template-columns: min-content auto min-content;
        width: 100%;
      }
    `;
  }

  static get formAssociated() {
    return true;
  }

  constructor() {
    super();
    this.#internals = this.attachInternals();
  }

  #internals: IElementInternals;
  #input = createRef<HTMLInputElement>();
  #form: HTMLFormElement;

  @property() name: string;
  @property() label: string;
  @property() tooltip: string;

  @property() expanded = false;

  @property({ type: Boolean, reflect: true })
  checked: boolean;

  get id() {
    return this.name + "-checkbox";
  }

  formResetCallback() {
    this.checked = false;
  }

  formAssociatedCallback(form: HTMLFormElement) {
    this.#form = form;
    this.#form.addEventListener("formdata", (e) => this.#onFormData(e));
  }

  #onFormData(e: FormDataEvent) {
    if (this.#input.value.checked && this.name) {
      e.formData.append(this.name, "true");
    }
  }

  #onInput(e: Event) {
    const element = e.target as HTMLInputElement;
    for (const item of this.items) {
      item.checked = element.checked;
    }
    this.checked = element.checked;
  }

  #onSlotchange(e: Event) {
    this.requestUpdate();
  }

  @queryAssignedElements({
    flatten: true,
    selector: "ac101-checkbox",
  })
  items!: Array<AC101Checkbox>;

  #renderToggle() {
    return html`<button class="toggle-button" @click=${this.toggle}>
      ${when(
        this.expanded,
        () => chevron(false),
        () => chevron(true)
      )}
    </button>`;
  }

  toggle() {
    this.expanded = !this.expanded;
  }

  render() {
    const hasChildren = this.items.length > 0;

    const slot = html`<slot @slotchange=${this.#onSlotchange}></slot>`;

    return html`<div
      class="filter-modal-checkbox-control select-checkbox-control "
    >
      <div class="d-flex justify-content-between align-items-center">
        <div class="filter-item-checkbox form-check" style="">
          <input
            ${ref(this.#input)}
            class="form-check-input"
            type="checkbox"
            id=${this.id}
            name=${this.name}
            .checked=${this.checked}
            @input=${this.#onInput}
          />
          <label class="form-check-label d-inline-block" for="${this.id}">
            ${this.label}
            <ac101-tooltip tooltip=${this.tooltip} class="d-inline-block">
              <div class="hover-text">${IconWarning}</div>
            </ac101-tooltip>
          </label>

          ${when(hasChildren, () => this.#renderToggle())}
        </div>
      </div>
      ${when(
        hasChildren,
        () =>
          html`<div class="ms-4 mt-2 ${this.expanded ? "" : "d-none"}">
            ${slot}
          </div>`,
        () => slot
      )}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ac101-checkbox": AC101Checkbox;
  }
}
